import { Injectable, inject } from '@angular/core'
import { ParamMap, Router } from '@angular/router'
import { LicApiCallService } from './lic-api-call.service'
import { Observable, catchError, delay, map, of, switchMap, tap } from 'rxjs'
import { OrderService } from './order/order.service'
import { UserStateService } from '../store/state/user/user.state.service'
import { LiveLocationService } from './live-location.service'
import { LicOrderRequest } from '../models/order/lic/lic.model'
import { LicService } from './lic-service'
import { OrderResumeStateService } from '../store/state/lic/order-resume-state-service'
import { PENDING_LAUNCH_ID_KEY, LAUNCH_ID_KEY, getPendingLaunchId } from '../util/session-util'


@Injectable({ providedIn: 'root' })
export class LaunchAuthRedirectService {
  #orderService = inject(OrderService)
  #licService = inject(LicService)
  #orderResumeStateService = inject(OrderResumeStateService)
  #router = inject(Router)
  #locationService = inject(LiveLocationService)
  #userStateService = inject(UserStateService)
  #licApiCallService = inject(LicApiCallService)

  captureFromQueryParams(queryParamMap: ParamMap): string | null {
    const launchId = this.#getLaunchId(queryParamMap)
    if (launchId) {
      this.setPendingLaunchId(launchId)
    }
    return launchId
  }

  setPendingLaunchId(launchId: string | null | undefined): void {
    const normalizedLaunchId = launchId?.trim()
    if (!normalizedLaunchId) {
      return
    }
    sessionStorage.setItem(PENDING_LAUNCH_ID_KEY, normalizedLaunchId)
    sessionStorage.setItem(LAUNCH_ID_KEY, normalizedLaunchId)
  }


  clearPendingLaunchId(): void {
    sessionStorage.removeItem(PENDING_LAUNCH_ID_KEY)
  }

  handlePendingLaunchId(ignoreApiCalls = false): Observable<string> {
    const launchId = getPendingLaunchId()
    if (!launchId) {
      return of(null)
    }

    const device = this.#locationService.coordinates()
    const request = {
      UserId: this.#userStateService.getUser()?.UserId,
      Latitude: device?.latitude?.toString() ?? '0',
      Longitude: device?.longitude?.toString() ?? '0',
      GeoAccuracy: device?.accuracy
    } as LicOrderRequest

    this.clearPendingLaunchId()
    const launch$ = this.#orderService.processLaunchInContextId(launchId, request)
      .pipe(
        catchError((error: any) => { throw error }),
        tap(response => this.#orderResumeStateService.setLicPayload(response)),
        delay(2000),
        switchMap(() => this.#licService.processResponse()),
        switchMap((response) => ignoreApiCalls ? of({}) : this.#licApiCallService.processApiCalls(response)),
        map(() => { return this.determineRedirect() }),
      )
    return launch$
  }

  private determineRedirect(): string | null {
    return this.#licService.getLicLandingPage();
  }

  #getLaunchId(queryParamMap: ParamMap): string | null {

    return queryParamMap.get('launchId')
      ?? queryParamMap.get('launchid')
      ?? queryParamMap.get('launch_id')
      ?? queryParamMap.get('launch-id')
  }
}
